import { useEffect, useState } from 'react';
import type { DeltaBundle, SatelliteIndex } from '@greeniq/shared';
import { getDeltas } from '../api/satellite.js';
import { INDEX_META } from '../lib/satelliteColors.js';

const ALL_INDICES: SatelliteIndex[] = ['ndvi', 'ndre', 'gndvi', 'savi', 'msavi', 'ndwi'];

interface Props {
  courseSlug: string;
  zoneId: number;
  /** Called when the user clicks an index label; parent opens the drawer. */
  onExplain?: (index: SatelliteIndex) => void;
}

type Window = 'wow' | 'mom' | 'yoy';
const WINDOW_LABELS: Record<Window, string> = {
  wow: 'WoW',
  mom: 'MoM',
  yoy: 'YoY',
};

export function DeltaChips({ courseSlug, zoneId, onExplain }: Props) {
  const [deltas, setDeltas] = useState<DeltaBundle | null>(null);
  const [atDate, setAtDate] = useState<string | null>(null);
  const [activeWindow, setActiveWindow] = useState<Window>('wow');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await getDeltas(courseSlug, zoneId);
        if (cancelled) return;
        setDeltas(resp.deltas);
        setAtDate(resp.atDate);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug, zoneId]);

  if (loading) return null;
  if (!deltas || !atDate) return null;

  return (
    <section className="mt-6 p-5 bg-bg-card rounded-lg border border-border">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm uppercase tracking-wider text-fg-muted font-semibold">
            Change vs baseline
          </h2>
          <p className="mt-1 text-xs text-fg-muted">At {atDate}</p>
        </div>
        <div className="inline-flex rounded-md bg-bg-elevated p-0.5">
          {(['wow', 'mom', 'yoy'] as const).map((w) => (
            <button
              key={w}
              onClick={() => setActiveWindow(w)}
              className={[
                'px-3 py-1.5 min-h-[32px] text-xs font-semibold rounded',
                activeWindow === w ? 'bg-bg-card shadow text-fg' : 'text-fg-secondary',
              ].join(' ')}
              aria-pressed={activeWindow === w}
            >
              {WINDOW_LABELS[w]}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {ALL_INDICES.map((idx) => (
          <DeltaChip
            key={idx}
            index={idx}
            deltaPct={deltas[idx][activeWindow].deltaPct}
            baselineValue={deltas[idx][activeWindow].baselineValue}
            baselineDate={deltas[idx][activeWindow].baselineDate}
            onExplain={onExplain ? () => onExplain(idx) : undefined}
          />
        ))}
      </div>
    </section>
  );
}

function DeltaChip({
  index,
  deltaPct,
  baselineValue,
  baselineDate,
  onExplain,
}: {
  index: SatelliteIndex;
  deltaPct: number | null;
  baselineValue: number | null;
  baselineDate: string | null;
  onExplain?: () => void;
}) {
  const meta = INDEX_META[index];
  const hasData = deltaPct !== null;
  const isDrop = hasData && deltaPct! < 0;
  const isSpike = hasData && deltaPct! > 0;
  const severityColor =
    !hasData
      ? 'text-fg-muted'
      : Math.abs(deltaPct!) >= 15
        ? 'text-[color:var(--color-error)]'
        : Math.abs(deltaPct!) >= 5
          ? 'text-greeniq-400'
          : 'text-greeniq-300';
  const arrow = !hasData ? '—' : isDrop ? '▼' : isSpike ? '▲' : '·';

  return (
    <div className="p-3 rounded-md bg-bg-elevated min-h-[64px] flex flex-col justify-between">
      <div className="flex items-baseline justify-between">
        <button
          type="button"
          onClick={onExplain}
          className="text-xs uppercase tracking-wider font-semibold text-fg-muted hover:text-fg focus:outline-none"
          style={onExplain ? { cursor: 'help' } : {}}
          aria-label={`Explain ${meta.label}`}
          disabled={!onExplain}
        >
          {meta.label}
        </button>
        <span className={`text-sm font-semibold ${severityColor}`}>
          <span className="mr-0.5">{arrow}</span>
          {hasData ? `${deltaPct! > 0 ? '+' : ''}${deltaPct!.toFixed(1)}%` : '—'}
        </span>
      </div>
      <div className="mt-1 text-[10px] text-fg-muted">
        {hasData && baselineDate
          ? `vs ${baselineValue!.toFixed(3)} on ${baselineDate}`
          : 'No baseline in range'}
      </div>
    </div>
  );
}
